import React, { useEffect, useState } from "react";
import to from "await-to-js";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";
import { Button } from "@material-ui/core";
import csvDownload from 'json-to-csv-export';
import * as Sentry from "@sentry/react";
import "jspdf-autotable";

import { accessPermission } from "app/common/helperFunction";
import { getBillValidators } from "app/services/billValidator";
import { getScope, hasMultipleAccounts } from "app/services/helpers/account-id";
import LoadingSpinner from "app/partials/content/LoadingSpinner";
import { useCustomResponse } from "app/hooks/useCustomResponse";

import ResponseModal from "../Helpers/ResponseModal";
import ExportAndCustomFilters from "../../partials/content/ExportAndCustomFilters";
import Search from "../../partials/content/Customers/Search";
import BillValidatorsTable from "./components/BillValidatorsTable/BillValidatorsTable";
import OperatorSpecificModal from "../Helpers/OperatorSpecificModal";
import AddBillValidator from "./AddBillValidator";

import styles from "./add-ons.module.scss";
import { ALL_LOCATIONS, ALL_OPERATORS, CASHCOLLECTOR_REPORT_NAME_PREFIX } from "app/common/constant";
import { ROLE } from "app/common/roleConstant";
import { getCashCollectorReports } from "app/services/reports";
import { triggerReportDownload } from "app/services/helpers/reportDownload";

export default function CashCollections(props) {
  const { filter: {location, operator} } = props;
  const { push } = useHistory();
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
  });
  const [cashCollections, setCashCollections] = useState([]);
  const [cashCollectionsTotal, setCashCollectionsTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [addBillValidatorPopup, setAddBillValidatorPopup] = useState(false);
  const [operatorSpecificModalOpen, setOperatorSpecificModalOpen] = useState(false);
  const [responseValue, setResponseValue] = useCustomResponse()

  const isSuperAdmin = getScope() === ROLE.SUPERADMIN


  useEffect(() => {
    if (!accessPermission("LedgerAll")) {
      push({ pathname: "/dashboard" });
    }
  }, []);

  /**
   * Get all Add-Ons
   */
  useEffect(() => {
    proceedBillValidators();
    // eslint-disable-next-line
  }, [pagination, searchQuery]);

  /**
   * Get all Add-Ons
   * @returns {Promise<void>}
   */
  async function proceedBillValidators() {

    if(hasMultipleAccounts()){
      setOperatorSpecificModalOpen(true);
      return;
    }

    const [err, res] = await to(getBillValidators(pagination, Boolean(location === ALL_LOCATIONS.id), Boolean(operator === ALL_OPERATORS.id), searchQuery));
    setIsLoaded(true);
    if (err) {     
      Sentry.captureException(err);
      setResponseValue({
        ...responseValue,  
        isMessageOpen: true,
        isSuccess: false,
        message: `Error while getting Bill Validators. ${err}`
      })
      return console.error(
        "Error while fetching Add-ons Bill Validators data:",
        err
      );
    }

    const {
      billValidator,
      meta: { totalItems },
    } = res?.data;
    
    setCashCollections(billValidator);
    setCashCollectionsTotal(totalItems);
  }

  function handleEditRowClick(id) {
    push({ pathname: `/cashCollections/${id}` });
  }

  function handleAddBillValidator() {
    setAddBillValidatorPopup(!addBillValidatorPopup);
  }

  async function handleExport(type) {
    if (cashCollections.length === 0) return;
    getCashCollectorReports(type) .then((response) => {
      triggerReportDownload(response,type,CASHCOLLECTOR_REPORT_NAME_PREFIX)
     }) .catch((err) => {
       Sentry.captureException(err)
       setResponseValue({
         ...responseValue,  
         isMessageOpen: true,
        isSuccess:false,
       message:`We had a problem downloading Customers Data`
     });
       console.error("An error occurred:", err);
     });
    
  }

  return (
    <>
      <div id="cash_collections_wrapper" className={styles.wrapper}>
    <ExportAndCustomFilters className={isSuperAdmin && styles.wrapper} onExport={handleExport} hasFilters={false} />
     {
      isSuperAdmin && <Search
      id="billValidator_search"
      label="Search by bill validator id"
      placeholder="Search"
      query={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
    />
     }
      </div>
      {!isLoaded ? (
        <LoadingSpinner/>
      ) : (
        <>
          <BillValidatorsTable
            rows={cashCollections}
            pagination={pagination}
            cashCollectionsTotal={cashCollectionsTotal}
            onEditRowClick={handleEditRowClick}
            onPaginationChange={(value) => setPagination(value)}
          />

          {getScope() === ROLE.SUPERADMIN && (<div id="cash_collections_button_wrapper" className={styles.buttonWrapper}>
            <Button
              id="cash_collections_add_biller_button"
              size="large"
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => handleAddBillValidator()}
            >
              Add Bill Validator
            </Button>
          </div>
           )};
          {addBillValidatorPopup && (
            <AddBillValidator
              setAddLockPopup={setAddBillValidatorPopup}
              proceedBillValidators={() => proceedBillValidators()}
            />
          )}
         
          <ResponseModal
            isSuccess={responseValue?.isSuccess}
            message={responseValue?.message}
            open={responseValue?.isMessageOpen}
            onClose={(value) => setResponseValue({...responseValue, isMessageOpen:value})}
          />
        </>
      )}
      {operatorSpecificModalOpen && (
        <OperatorSpecificModal
          open={operatorSpecificModalOpen}
          onClose={(value) => setOperatorSpecificModalOpen(value)}
        />
      )}
    </>
  );
}
